import { useEffect } from "react";
import toast from "react-hot-toast";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { LoadingButton } from "@mui/lab";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";

import { Seo } from "src/components/seo";
import { withAuthGuard } from "src/hocs/with-auth-guard";
import { Layout as ClassicLayout } from "src/layouts/auth/classic-layout";
import { useAuth } from "src/hooks/use-auth";
import { useNetworkAccounts } from "src/hooks/use-network-accounts";
import { usePageView } from "src/hooks/use-page-view";
import { useRouter } from "src/hooks/use-router";
import { paths } from "src/paths";
import {
  groupAccountsByNetwork,
  takeNetworkExpiredNotice,
} from "src/utils/snug/network-session";

// Where a network admin lands after signing in: their own login has no pro group, so they
// pick one to switch into. Lives outside the dashboard layout and loads no pro group data.
const Page = () => {
  const router = useRouter();
  const { signOut } = useAuth();
  const { network, loading, error, switchingRoleId, switchTo } = useNetworkAccounts();

  usePageView("Pro - Network");

  useEffect(() => {
    if (takeNetworkExpiredNotice()) {
      toast("Pick a pro group to continue", { id: "network-expired" });
    }
  }, []);

  // Not a network login at all (a network admin with no pro groups yet stays here, on the empty state)
  useEffect(() => {
    if (!loading && !error && network && !network.is_network_admin) {
      router.replace(paths.dashboard.index);
    }
  }, [loading, error, network, router]);

  const handleSignOut = async () => {
    await signOut();
    router.replace(paths.auth.jwt.entryStart);
  };

  const groups = groupAccountsByNetwork(network?.accounts || []);

  return (
    <>
      <Seo title="Choose a pro group" />
      <Stack spacing={3}>
        <div>
          <Typography variant="h5">Choose a pro group</Typography>
          <Typography
            color="text.secondary"
            sx={{ mt: 1 }}
            variant="body2"
          >
            {network?.home_email
              ? `Signed in as ${network.home_email}. Switch into a pro group to manage it.`
              : "Switch into a pro group to manage it."}
          </Typography>
        </div>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && (
          <Alert severity="error">
            We couldn&apos;t load your pro groups. Refresh the page to try again.
          </Alert>
        )}

        {!loading && !error && network?.is_network_admin && network.accounts.length === 0 && (
          <Card
            variant="outlined"
            sx={{ border: "1px solid #e7e7e7", borderRadius: 1, p: 4, textAlign: "center" }}
          >
            <Typography variant="subtitle1">No sub groups yet</Typography>
            <Typography
              color="text.secondary"
              sx={{ mt: 1 }}
              variant="body2"
            >
              Snug is setting up the pro groups in your network. They&apos;ll appear here once they&apos;re ready.
            </Typography>
          </Card>
        )}

        {groups.map((group) => (
          <Stack
            key={group.network_id}
            spacing={1.5}
          >
            <Typography
              color="text.secondary"
              variant="overline"
            >
              {group.network_name}
            </Typography>
            <Card
              variant="outlined"
              sx={{ border: "1px solid #e7e7e7", borderRadius: 1 }}
            >
              {group.accounts.map((account, index) => {
                const isCurrent =
                  !!network?.switched && network?.current_ud_id === account.owner_ud_id;
                return (
                  <Box key={account.role_id}>
                    {index > 0 && <Divider />}
                    <Stack
                      alignItems="center"
                      direction="row"
                      spacing={2}
                      sx={{ px: 2, py: 1.5 }}
                    >
                      <Avatar
                        src={account.logo || undefined}
                        variant="rounded"
                        sx={{ bgcolor: "neutral.100", color: "text.secondary", width: 40, height: 40 }}
                        imgProps={{ style: { objectFit: "contain" } }}
                      >
                        <BusinessOutlinedIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          noWrap
                          variant="subtitle2"
                        >
                          {account.pro_group_name}
                        </Typography>
                        {account.owner_email && (
                          <Typography
                            color="text.secondary"
                            noWrap
                            variant="caption"
                            component="div"
                          >
                            Signs in as {account.owner_email}
                          </Typography>
                        )}
                      </Box>
                      <LoadingButton
                        disabled={isCurrent || (!!switchingRoleId && switchingRoleId !== account.role_id)}
                        loading={switchingRoleId === account.role_id}
                        onClick={() => switchTo(account.role_id)}
                        size="small"
                        variant={isCurrent ? "text" : "contained"}
                      >
                        {isCurrent ? "Current" : "Switch"}
                      </LoadingButton>
                    </Stack>
                  </Box>
                );
              })}
            </Card>
          </Stack>
        ))}

        <Box sx={{ textAlign: "center" }}>
          <Link
            color="text.secondary"
            component="button"
            onClick={handleSignOut}
            type="button"
            underline="hover"
            variant="caption"
          >
            Sign out
          </Link>
        </Box>
      </Stack>
    </>
  );
};

const NetworkPage = withAuthGuard(() => (
  <ClassicLayout>
    <Page />
  </ClassicLayout>
));

export default NetworkPage;
